import { AnnouncementBanner, LiveChatInitialiser, SkipLink, Wrapper } from '@ahmdigital/ui';
import { includes } from 'lodash/fp';
import { ReactNode, useRef } from 'react';
import { usePathname } from 'next/navigation';
import cookie from 'cookie';
import webViewUtils from '@ahmdigital/logic/lib/utils/web-view';

import AppHead from './components/app-head';
import browserUtil from '../utils/browser';
import CMSAnnouncementBanner from '../components/cms-announcement-banner';
import CMSOverlay from '../components/cms-overlay';
import config from '../config';
import constants from '../ahm-constants';
import CookieDisclaimerBanner from './components/cookie-disclaimer-banner';
import Footer from '../elements/footer';
import FooterLinks from '../elements/footer-links';
import FooterPurchase from '../elements/footer-purchase';
import getIsProductionEnv from '../utils/get-is-production-env';
import Header from '../elements/header';
import isFeatureEnabled from '../utils/is-feature-enabled';
import Toaster from './components/toaster';
import useDelaconScript from '../analytics/delacon';
import useIsAfterHydration from '../hooks/use-is-after-hydration';

const ApplicationElement = ({ children }: { children: ReactNode }) => {
  const footerRef = useRef<HTMLDivElement | null>(null);
  const cmsAnnouncementBannerRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const isAfterHydration = useIsAfterHydration();

  // Delacon script patches phone numbers on client side, only run after hydration.
  useDelaconScript(isAfterHydration);

  const doc = browserUtil.getDocument();

  if (isAfterHydration && webViewUtils.isWebView(cookie.parse(doc?.cookie ?? ''))) {
    return (
      <>
        <AppHead />
        <main id="main" role="main">
          {children}
        </main>
        <LiveChatInitialiser suffix="DesktopSales" />
        <LiveChatInitialiser suffix="MobileSales" />
      </>
    );
  }

  const isPurchaseFunnel = includes(pathname, [
    constants.URLS.SALES.BUY,
    constants.URLS.SALES.BUY_ABOUT,
    constants.URLS.SALES.BUY_AGR,
    constants.URLS.SALES.BUY_COVER,
    constants.URLS.SALES.BUY_PAYMENT,
  ]);
  const FooterElement = isPurchaseFunnel ? FooterPurchase : Footer;
  const announcementBannerIsVisible =
    isFeatureEnabled('announcementBanner') && includes(pathname, constants.ANNOUNCEMENT_BANNER_ALLOWLIST);
  const securityAnnouncementBannerIsVisible = includes(pathname, ['/security-privacy']);

  return (
    <>
      <AppHead />
      {isAfterHydration && !getIsProductionEnv() && <CMSOverlay />}
      <SkipLink to="#main">Skip to main content</SkipLink>
      {announcementBannerIsVisible && (
        <AnnouncementBanner message={config.get('features:announcementBanner:message')} />
      )}
      {securityAnnouncementBannerIsVisible && (
        <AnnouncementBanner message="If you get any suspicious emails or texts, report them to [Scamwatch.](https://www.scamwatch.gov.au/report-a-scam)" />
      )}
      {isAfterHydration && (
        <div ref={cmsAnnouncementBannerRef}>
          <CMSAnnouncementBanner />
        </div>
      )}
      <Toaster />
      <Wrapper size="extraExtraLarge" isFullWidth>
        <Header isMinimalMenu={isPurchaseFunnel} />
      </Wrapper>
      {isAfterHydration && <CookieDisclaimerBanner />}
      <div>
        <div id="main" role="main" style={{ display: 'block', outline: 0 }} tabIndex={-1}>
          {children}
        </div>
        {isAfterHydration && <LiveChatInitialiser suffix="DesktopSales" />}
        {isAfterHydration && <LiveChatInitialiser suffix="MobileSales" />}

        <Wrapper size="extraExtraLarge" isFullWidth>
          <div ref={footerRef}>
            <FooterElement />
          </div>
          <FooterLinks footerRef={footerRef} cmsAnnouncementBannerRef={cmsAnnouncementBannerRef} />
        </Wrapper>
      </div>
    </>
  );
};

export default ApplicationElement;
